import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLandingConfig } from "@/hooks/useLandingConfig";

// Sezione Partner sopra il footer della landing — appare solo se ci sono partner attivi.
// Layout raffinato: logo tondo + nome + descrizione, griglia responsive.
// Sorgente dati: /api/public/partners (partner attivi da db.partners, gestiti in AdminDashboard → Panoramica → Partner).
// Se manca il logo_url e il website punta a Instagram, mostra un placeholder Instagram-style con handle.
// (mshots.wordpress.com blocca Instagram, quindi manteniamo un fallback professionale in-app.)

function getInstagramHandle(url) {
  if (!url || typeof url !== "string") return null;
  const m = url.match(/instagram\.com\/([A-Za-z0-9_.]+)/i);
  if (!m) return null;
  const handle = m[1].replace(/\/$/, "");
  return handle && handle.length > 0 ? handle : null;
}

function InstagramIcon(props) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <defs>
        <linearGradient id="ig-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#F58529" />
          <stop offset="0.35" stopColor="#DD2A7B" />
          <stop offset="0.7" stopColor="#8134AF" />
          <stop offset="1" stopColor="#515BD4" />
        </linearGradient>
      </defs>
      <rect x="2" y="2" width="20" height="20" rx="5" fill="url(#ig-grad)"/>
      <circle cx="12" cy="12" r="4.2" fill="none" stroke="#fff" strokeWidth="1.6"/>
      <circle cx="17.5" cy="6.5" r="1.1" fill="#fff"/>
    </svg>
  );
}

function PartnerAvatar({ partner }) {
  const [errored, setErrored] = useState(false);
  const igHandle = getInstagramHandle(partner.website);
  const showLogo = partner.logo_url && !errored;

  if (showLogo) {
    return (
      <div className="aspect-square max-w-[180px] mx-auto rounded-full bg-background border border-border/50 flex items-center justify-center overflow-hidden transition-all group-hover:scale-105 group-hover:border-mauve">
        <img
          src={partner.logo_url}
          alt={partner.name}
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setErrored(true)}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  // Fallback quando manca il logo: Instagram-style card se abbiamo l'handle IG,
  // altrimenti iniziali del nome.
  if (igHandle) {
    return (
      <div className="aspect-square max-w-[180px] mx-auto rounded-full flex flex-col items-center justify-center gap-2 overflow-hidden transition-all group-hover:scale-105 group-hover:shadow-lg bg-gradient-to-br from-[#fef7f4] via-[#fce9f0] to-[#e9e0f7] border border-border/50">
        <InstagramIcon className="w-9 h-9" />
        <span className="font-serif-display text-lg text-foreground/85 leading-none">@{igHandle}</span>
        <span className="text-[9px] uppercase tracking-widest text-foreground/40">Su Instagram</span>
      </div>
    );
  }

  return (
    <div className="aspect-square max-w-[180px] mx-auto rounded-full bg-background border border-border/50 flex items-center justify-center overflow-hidden transition-all group-hover:scale-105 group-hover:border-mauve">
      <span className="font-serif-display text-3xl text-foreground/60">
        {(partner.name || "?").slice(0, 2).toUpperCase()}
      </span>
    </div>
  );
}

export default function LandingSponsors() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const { T } = useLandingConfig();
  const eyebrow = T("texts.partners.eyebrow", "Con il supporto di");
  const t1 = T("texts.partners.title_line1", "I");
  const t2 = T("texts.partners.title_line2", "partner");
  const t3 = T("texts.partners.title_line3", "che credono in noi.");
  const italicWord = T("texts.partners.title_italic_word", "partner");

  useEffect(() => {
    api.get("/public/partners")
      .then(r => setPartners(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPartners([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading || partners.length === 0) return null;

  // Componi titolo con italic mauve sulla parola scelta
  const fullTitle = `${t1} ${t2} ${t3}`.trim();
  const renderTitle = () => {
    if (!italicWord || !fullTitle.toLowerCase().includes(italicWord.toLowerCase())) {
      return fullTitle;
    }
    const idx = fullTitle.toLowerCase().indexOf(italicWord.toLowerCase());
    return (
      <>
        {fullTitle.slice(0, idx)}
        <span className="font-editorial text-mauve">{fullTitle.slice(idx, idx + italicWord.length)}</span>
        {fullTitle.slice(idx + italicWord.length)}
      </>
    );
  };

  return (
    <section data-testid="landing-partners" className="relative bg-muted/30 border-t border-border/50">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="text-center mb-16 md:mb-20">
          <div className="text-[10px] uppercase tracking-[0.42em] text-foreground/50 mb-6 flex items-center justify-center gap-3">
            <span className="w-10 h-px bg-foreground/40" />
            {eyebrow}
            <span className="w-10 h-px bg-foreground/40" />
          </div>
          <h2 className="font-serif-display font-normal text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-foreground max-w-2xl mx-auto">
            {renderTitle()}
          </h2>
        </div>

        <div className="flex flex-wrap justify-center gap-8 md:gap-12 lg:gap-16">
          {partners.map((p) => {
            const href = p.website || p.website_url || null;
            const Card = ({ children }) => (
              href ? (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`partner-${p.id}`}
                  className="group block text-center transition-all w-40 sm:w-48 md:w-52 lg:w-56"
                >
                  {children}
                </a>
              ) : (
                <div data-testid={`partner-${p.id}`} className="text-center group w-40 sm:w-48 md:w-52 lg:w-56">
                  {children}
                </div>
              )
            );
            return (
              <Card key={p.id}>
                <PartnerAvatar partner={p} />
                <div className="mt-5">
                  <div className="font-serif-display text-xl md:text-2xl leading-tight text-foreground">
                    {p.name}
                  </div>
                  {p.description && (
                    <p className="mt-2 text-xs md:text-sm text-foreground/60 leading-relaxed font-light">
                      {p.description}
                    </p>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}
